import React from "react";
import { Collection, FileEarmarkText } from "react-bootstrap-icons";
import { useSearchParams } from "react-router-dom";
import {
  defaultPlatformTab,
  getPlatformTabBySlug,
  PlatformBlock,
  PlatformTable,
  platformTabs,
} from "./platform.content";
import { PlatformMigrationForm } from "./platform-form";
import styles from "./platform.module.scss";

export const resolveExhibitionsHref = (_hostname: string = window.location.hostname): string =>
  "/exhibitions/mom";

const renderTable = (table: PlatformTable) => (
  <div className={styles.tableScroller}>
    <table className={styles.table}>
      {table.columnWidths ? (
        <colgroup>
          {table.columnWidths.map((width, index) => (
            <col key={`${table.caption}-${index}`} style={{ width }} />
          ))}
        </colgroup>
      ) : null}
      <caption>{table.caption}</caption>
      <thead>
        <tr>
          {table.headers.map((header) => (
            <th key={header} scope="col">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row.join("|")}>
            {row.map((cell, cellIndex) => {
              const isRowHeader = table.rowHeaderColumn === cellIndex;
              if (isRowHeader) {
                return (
                  <th key={`${cell}-${cellIndex}`} scope="row">
                    {cell}
                  </th>
                );
              }

              return <td key={`${cell}-${cellIndex}`}>{cell}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const renderBlock = (block: PlatformBlock, index: number) => {
  if (block.type === "paragraph") {
    return <p key={index}>{block.text}</p>;
  }

  if (block.type === "list") {
    const ListTag = block.ordered ? "ol" : "ul";
    return (
      <ListTag key={index} className={styles.list}>
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ListTag>
    );
  }

  if (block.type === "table") {
    return <React.Fragment key={index}>{renderTable(block.table)}</React.Fragment>;
  }

  if (block.type === "form") {
    return <PlatformMigrationForm key={index} form={block.form} />;
  }

  return (
    <div key={index} className={styles.callout} role="note">
      <strong>{block.title}</strong>
      {block.body.startsWith("Spatial public notice: ") ? (
        <p>
          Spatial public notice:{" "}
          <a href="https://www.spatial.io/blog/spatial-creator-platform-sunsetting" target="_blank" rel="noreferrer">
            spatial.io creator platform sunsetting
          </a>
        </p>
      ) : (
        <p>{block.body}</p>
      )}
    </div>
  );
};

export const PlatformPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getPlatformTabBySlug(searchParams.get("tab"));
  const exhibitionsHref = resolveExhibitionsHref();

  const onSelectTab = (slug: string) => {
    if (slug === defaultPlatformTab.slug) {
      setSearchParams({});
      return;
    }

    setSearchParams({ tab: slug });
  };

  return (
    <main className={styles.page}>
      <div className={styles.layout}>
        <section className={styles.hero} aria-labelledby="platform-title">
          <div className={styles.heroText}>
            <p className={styles.kicker}>Public platform documentation</p>
            <h1 id="platform-title">Artetra Platform</h1>
            <p>
              Product maturity, release history, public roadmap, workflows, and migration guidance for browser-based
              cultural heritage publishing.
            </p>
          </div>
          <div className={styles.heroActionPanel} aria-label="Artetra exhibitions access">
            <span>Public galleries</span>
            <strong>Open Artetra Exhibitions in a browser-based gallery view.</strong>
            <a className={styles.primaryLink} href={exhibitionsHref}>
              <Collection aria-hidden="true" />
              Artetra Exhibitions
            </a>
          </div>
        </section>

        <div className={styles.workspace}>
          <nav className={styles.tabs} role="tablist" aria-label="Platform sections">
            {platformTabs.map((tab) => {
              const isActive = activeTab.id === tab.id;
              return (
                <button
                  key={tab.id}
                  id={`platform-tab-${tab.slug}`}
                  className={isActive ? styles.activeTab : styles.tab}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`platform-panel-${tab.slug}`}
                  onClick={() => onSelectTab(tab.slug)}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <section
            id={`platform-panel-${activeTab.slug}`}
            className={styles.panel}
            role="tabpanel"
            aria-labelledby={`platform-tab-${activeTab.slug}`}
          >
            <div className={styles.panelHeader}>
              <FileEarmarkText aria-hidden="true" />
              <div>
                <h2>{activeTab.title}</h2>
                <p>{activeTab.summary}</p>
              </div>
            </div>
            <div className={styles.sections}>
              {activeTab.sections.map((section) => (
                <section key={section.heading} className={styles.contentSection}>
                  <h3>{section.heading}</h3>
                  {section.blocks.map(renderBlock)}
                </section>
              ))}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PlatformPage;
